/* eslint-disable max-len */
import React, { useContext, useCallback, useMemo } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FontIcon } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Collapse, TextTitle, useCollapseState, TitleBarError } from '@bingads-webui-campaign/wizard-jumbo-card';

import InterestIcon from '../media/interest.svg';
import DemographicIcon from '../media/demographic.svg';
import SparkleImg from '../media/sparkle.svg';

import { AudienceInterests } from './audience-interests';
import { AudienceDemographics } from './audience-demographics';
import { useTargetInstrument } from '../use-target-instrument';
import { getDemographicsDetails } from '../utils/common';

export const CollapsibleAudienceSignal = ({
  classes,
  isLoading,
  data,
  onDataChange,
}) => {
  const i18n = useContext(I18nContext);
  const { audienceSignalLogger } = useTargetInstrument();

  const { audiencesSignal } = data || {};
  const {
    interests = [],
    ageRanges = [],
    genderTypes = [],
    genderErrors,
    ageErrors,
  } = audiencesSignal || {};

  const [
    allSections,
    expandedSections,
    setExpandedSections,
  ] = useCollapseState(
    ['interests', 'demographics'],
    ['interests', 'demographics']
  );

  const interestDetails = useMemo(() => (
    <div className={classNames('audience-interests', classes.interestDetails)}>
      {
        _.map(interests, interest => (
          <span className="audience-interest" key={interest}>{interest}</span>
        ))
      }
    </div>
  ), [classes, interests]);

  const demographicsDetails = useMemo(() => {
    const details = getDemographicsDetails(ageRanges, genderTypes, i18n);
    if (!details) {
      return null;
    }

    return (
      <div className={classNames('audience-demographics', classes.demographicsDetails)}>
        <FontIcon className="audience-icon" aria-label={i18n.getString(_TL_('Demographics'))} iconName="People" />
        <span>{details}</span>
      </div>
    );
  }, [ageRanges, genderTypes, i18n, classes]);

  const onAudienceChange = useCallback((newAudiences) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        audiencesSignal: {
          ...prevData.audiencesSignal,
          ...newAudiences,
        },
      }),
    });
  }, [onDataChange]);

  return (
    <div className="audience-signal-section">
      <div className={classes.descriptionContainer}>
        <h3 className="section-title">
          <span>{i18n.getString(_TL_('Audience signals'))} </span>
          <span className={classes.h3LabelOptional}>{i18n.getString(_TL_('(OPTIONAL)'))}</span>
        </h3>
        <div className={classNames(classes.audienceSignalDescription, 'section-description')}>
          <img src={SparkleImg} alt="" />
          <span className="audience-signal-main-description">{i18n.getString(_TL_('Performance Max finds the right audience for you'))}</span>
          <span className="audience-signal-sub-description">{i18n.getString(_TL_('We’ll use an audience signal to use as a starting point to reach the right customers.'))}</span>
        </div>
      </div>
      <Collapse allPanelItems={allSections} checkItems activeKeys={expandedSections} setActiveKeys={setExpandedSections}>
        <Collapse.Panel
          key="interests"
          title={<TextTitle
            titleKey={_TL_('Interest')}
            icon={InterestIcon}
            details={interestDetails}
          />}
        >
          <AudienceInterests
            isLoading={isLoading}
            classes={classes}
            data={audiencesSignal}
            onDataChange={onAudienceChange}
            logger={audienceSignalLogger}
          />
        </Collapse.Panel>
        <Collapse.Panel
          key="demographics"
          title={<TextTitle
            titleKey={_TL_('Demographics')}
            icon={DemographicIcon}
            details={demographicsDetails}
          />}
          error={!_.contains(expandedSections, 'demographics') && <TitleBarError error={genderErrors || ageErrors} />}
        >
          <AudienceDemographics
            isLoading={isLoading}
            classes={classes}
            data={audiencesSignal}
            onDataChange={onAudienceChange}
            logger={audienceSignalLogger}
          />
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

CollapsibleAudienceSignal.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  data: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    descriptionContainer: PropTypes.string.isRequired,
    h3LabelOptional: PropTypes.string.isRequired,
    audienceSignalDescription: PropTypes.string.isRequired,
    interestDetails: PropTypes.string.isRequired,
    demographicsDetails: PropTypes.string.isRequired,
  }).isRequired,
};

CollapsibleAudienceSignal.defaultProps = {};
